import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { startCase } from "lodash";
import classNames from "classnames";
import type { ColumnDef } from "@tanstack/react-table";
import { CheckCircleIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

import Label from "@components/form/Label";
import AuditTrailDetailModal from "@components/modal/AuditTrailDetailModal";
import ActionsButton from "@components/shared/DataTable/ActionsButton";
import DataTableRenderer from "@components/shared/DataTable/DataTableRenderer";
import { usePaginationState } from "@components/shared/DataTable/Pagination";
import PageHeading from "@components/zenith/ui/PageHeading";
import { useAppContext } from "@context/AppContext";
import { useAllEventTransactions } from "@hooks/query/useAllEventTransactions";
import { useAllEventTypes } from "@hooks/query/useAllEventTypes";
import { getEventTransactions } from "@services/eventTransactions";
import { formatDateTime } from "@utils/formatDateTime";

// The Audit Trail lists only these groups, so System events (logins, workflow
// bookkeeping, token transfers) stay off it. The API still shows subscribers
// their token transfers elsewhere, as in the zone account history.
const SUBSCRIBER_EVENT_GROUPS = ["User", "Accounting", "Exchange"];

// Matches react-select's default 38px control, which sits beside the date inputs.
const DATE_INPUT_CLASS =
  "rounded border border-gray-300 px-3 py-2 text-sm text-gray-600";

type Option = { value: string; label: string };

type AuditEvent = {
  id: string;
  eventType: string;
  description: string;
  user?: string;
  timestamp: string;
  onChain: boolean;
  childEventTransactions?: AuditEvent[];
};

const toAuditEvent = (event: any): AuditEvent => ({
  id: event.id,
  eventType: startCase(event.eventType?.name),
  description: event.description,
  user: event.createdByUser?.name,
  timestamp: formatDateTime(new Date(event.createdAt)),
  onChain: Boolean(event.hcsId),
  ...(event.childEventTransactions && {
    childEventTransactions: event.childEventTransactions.map(toAuditEvent),
  }),
});

// One CSV row per event; nested events follow their parent and name it.
const toExportRows = ({
  childEventTransactions = [],
  ...event
}: AuditEvent) => [
  event,
  ...childEventTransactions.map(child => ({ ...child, parentId: event.id })),
];

const AuditTrailIndex: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { loggedInInfo } = useAppContext();
  const walletId = loggedInInfo?.userDetails?.walletId;
  const [searchParams] = useSearchParams();
  const [viewId, setViewId] = useState(searchParams.get("id"));
  const [pagination, setPagination] = usePaginationState();
  const [eventType, setEventType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Any filter change returns to the first page.
  const withPageReset = (set: (value: string) => void) => (value: string) => {
    set(value);
    setPagination(current => ({ ...current, pageIndex: 0 }));
  };

  const { data: eventTypes = [] }: { data?: Option[] } = useAllEventTypes({
    select: (types: any[]): Option[] =>
      types
        .filter(type => SUBSCRIBER_EVENT_GROUPS.includes(type.eventGroup?.name))
        .map(type => ({ value: type.name, label: startCase(type.name) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    refetchOnWindowFocus: false,
  });

  // The API reads the dates as whole days in the browser's time zone.
  const params = {
    walletId,
    eventGroups: SUBSCRIBER_EVENT_GROUPS,
    eventTypes: eventType ? [eventType] : undefined,
    startDate,
    endDate,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  const { data, isLoading, isFetching } = useAllEventTransactions({
    params: {
      ...params,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    },
    enabled: Boolean(walletId),
    keepPreviousData: true,
  });
  const rows = React.useMemo(
    () => data?.eventTransactions.map(toAuditEvent) ?? [],
    [data],
  );
  const fetchAll = async () => {
    const all = await getEventTransactions({ ...params, limit: -1 });
    return { data: { data: all.eventTransactions.map(toAuditEvent) } };
  };

  const columns = React.useMemo<ColumnDef<AuditEvent>[]>(
    () => [
      {
        accessorKey: "eventType",
        header: t("audit_trail.event_type") as string,
        cell: ({ row, getValue }) => (
          // The API nests events one level deep, so a child takes one indent.
          <div
            className={classNames("inline-flex items-center gap-2", {
              "pl-8": row.depth > 0,
            })}
          >
            {row.getCanExpand() ? (
              <button
                type="button"
                aria-expanded={row.getIsExpanded()}
                aria-label={t("audit_trail.related_events") as string}
                onClick={row.getToggleExpandedHandler()}
              >
                <ChevronRightIcon
                  className={classNames("h-4 w-4", {
                    "rotate-90": row.getIsExpanded(),
                  })}
                  aria-hidden="true"
                />
              </button>
            ) : null}
            <span className="inline-block whitespace-nowrap rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-bold text-blue-800">
              {getValue() as string}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "description",
        header: t("audit_trail.description") as string,
        cell: context => (
          <div
            className="line-clamp-2 min-w-64"
            title={context.getValue() as string}
          >
            {context.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: "user",
        header: t("common.user") as string,
        cell: context => (
          <span className="whitespace-nowrap">
            {(context.getValue() as string) ?? t("audit_trail.system")}
          </span>
        ),
      },
      {
        accessorKey: "timestamp",
        header: t("audit_trail.timestamp") as string,
        cell: context => (
          <span className="whitespace-nowrap">
            {context.getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: "onChain",
        header: t("common.on_chain") as string,
        cell: context =>
          context.getValue() ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-green-600">
              <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
              {t("audit_trail.verified")}
            </span>
          ) : (
            <span className="whitespace-nowrap text-xs text-gray-500">
              {t("common.off_chain")}
            </span>
          ),
      },
      {
        id: "actions",
        header: t("common.actions") as string,
        cell: ({ row }) => (
          <ActionsButton
            menus={[
              { label: t("common.view"), onClick: () => setViewId(row.id) },
            ]}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <PageHeading
        title={t("audit_trail.audit_trail")}
        sub={t("audit_trail.subtitle")}
      />

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-wrap gap-2.5 border-b border-gray-200 px-5 py-3 text-sm">
          <div className="w-72">
            <Label htmlFor="audit-event-type">
              {t("audit_trail.event_type")}
            </Label>
            <Select
              inputId="audit-event-type"
              classNamePrefix="audit_event_type"
              // Above the table's sticky header (z-10), below the app bar (z-30).
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: 20 }) }}
              options={eventTypes}
              value={
                eventTypes.find(option => option.value === eventType) ?? null
              }
              onChange={option =>
                withPageReset(setEventType)(option?.value ?? "")
              }
              placeholder={t("audit_trail.all_event_types")}
              isClearable
            />
          </div>
          <div>
            <Label htmlFor="audit-start-date">
              {t("audit_trail.start_date")}
            </Label>
            <input
              id="audit-start-date"
              type="date"
              className={DATE_INPUT_CLASS}
              value={startDate}
              max={endDate || undefined}
              onChange={e => withPageReset(setStartDate)(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="audit-end-date">{t("audit_trail.end_date")}</Label>
            <input
              id="audit-end-date"
              type="date"
              className={DATE_INPUT_CLASS}
              value={endDate}
              min={startDate || undefined}
              onChange={e => withPageReset(setEndDate)(e.target.value)}
            />
          </div>
        </div>

        {/* Scrolls the table inside the card rather than widening the page on phones. */}
        <div className="flex flex-col gap-4 overflow-x-auto p-5">
          <DataTableRenderer
            name="audit-trail"
            data={rows}
            columns={columns}
            pagination={pagination}
            pageCount={data?.meta?.lastPage ?? -1}
            onPaginationChange={setPagination}
            isLoading={isLoading}
            isFetching={isFetching}
            fetchAll={fetchAll}
            searchable={false}
            subRowsKey="childEventTransactions"
            transformExportData={toExportRows}
          />
        </div>
      </section>

      {viewId ? (
        <AuditTrailDetailModal
          open
          id={viewId}
          onClose={() => setViewId(null)}
          closeOnError
        />
      ) : null}
    </div>
  );
};

export default AuditTrailIndex;
